/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * CreateEditRolePage
 *
 * React page for creating new application roles with full client-side + server-side
 * validation handling. Integrates tightly with CreateEditRoleComponent and is responsible for:
 * - Submitting validated form data to `/arcee/user/roles/create/`
 * - Showing progress state using ProgressBarDialog
 * - Mapping Django/DRF server validation errors back to the form (inline field errors)
 * - Handling AbortController for safe cancellation
 * - Displaying AlertDialog for success, network errors, and non-field validation issues
 * - Redirecting back to the application's role list after successful creation
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import CreateEditRoleComponent from '../components/applications/CreateEditRoleComponent';
import ProgressBarDialog from '../components/common/ProgressBar';
import AlertDialog from '../components/common/AlertDialog';
import { useQuery } from "@tanstack/react-query";

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

export default function CreateEditRolePage() {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { id, roleId } = useParams();
    const isEdit = !!roleId;
    const location = useLocation();
    const passedRole = location.state;

    const [progressOpen, setProgressOpen] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const controllerRef = useRef(null);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('info');

    const [serverErrors, setServerErrors] = useState(null);

    const [initialData, setInitialData] = useState(null);
    const loadedOnce = useRef(false)

    // React Query → Load single role only when needed
    const {
        data: fetchedRole,
        isLoading: roleLoading,
        isError: roleError,
        error: roleErrObj,
    } = useQuery({
        queryKey: ["singleRole", roleId],
        enabled: isEdit && !passedRole && !!roleId,
        retry: 0,
        queryFn: async ({ signal }) => {
            const resp = await apiHelper.apiCall(
                `/arcee/user/roles/${roleId}/details/`,
                {
                    method: "GET",
                    signal,
                }
            );

            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                const e = new Error("Failed to load role");
                e.info = errData;
                throw e;
            }

            return resp.json();
        },
    });

    useEffect(() => {
        if (passedRole) {
            // Coming from the list page → instant load
            setInitialData({
                name: passedRole.name,
                label: passedRole.label,
                description: passedRole.description ?? "",
                default_permissions: passedRole.default_permissions ?? [],
            });
        }
        else if (fetchedRole) {
            // Loaded from API
            setInitialData({
                name: fetchedRole.name,
                label: fetchedRole.label,
                description: fetchedRole.description ?? "",
                default_permissions: fetchedRole.default_permissions ?? [],
            });
        }
    }, [passedRole, fetchedRole]);

    // Show loader
    if (roleLoading) {
        return (
            <ProgressBarDialog
                open={true}
                text_message="Loading role..."
            />
        );
    }

    // Show error
    if (roleError) {
        return (
            <AlertDialog
                open={true}
                type="error"
                title={roleErrObj?.message || "Failed to load role"}
                message={
                    (roleErrObj?.info && roleErrObj.info.error) || "Could not load role details."
                }
                okLabel="Close"
                showCancel={false}
                onOk={() => navigate(`/dashboard/applications/${id}/roles`)}
                mini_mode={false}
            />
        );
    }

    // Called by CreateEditRoleComponent when user submits valid form
    const handleSubmit = async (payload) => {
        // ensure application id from route is present
        const appId = id || payload.application;
        if (!appId) {
            setAlertTitle('Missing Application');
            setAlertMessage('Application id is missing. Please access this page from the application context.');
            setAlertType('error');
            setAlertOpen(true);
            return;
        }

        if (submitting) return; // guard against duplicate submits
        setSubmitting(true);
        setProgressMessage('Creating role...');
        setProgressOpen(true);
        setServerErrors(null);

        controllerRef.current = new AbortController();
        const { signal } = controllerRef.current;

        // ensure payload contains application id expected by API
        const bodyPayload = { ...payload, application: appId };

        try {
            const url = isEdit ? `/arcee/user/roles/${roleId}/update/` : `/arcee/user/roles/create/`;
            const method = isEdit ? "PATCH" : "POST";
            const resp = await apiHelper.apiCall(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyPayload),
                signal,
            });

            if (!resp.ok) {
                let errBody;
                try {
                    errBody = await resp.json();
                } catch (jsonErr) {
                    try {
                        errBody = await resp.text();
                    } catch (textErr) {
                        errBody = null;
                    }
                }

                console.warn('Create role failed', { status: resp.status, body: errBody });

                // If the backend returned a dict of field errors (common DRF format),
                // map them to serverErrors to display inline in the form.
                if (errBody && typeof errBody === 'object' && !Array.isArray(errBody)) {
                    // Convert arrays to joined strings and also ensure keys match child field names
                    const mapped = {};
                    for (const [k, v] of Object.entries(errBody)) {
                        if (Array.isArray(v)) mapped[k] = v.join(' ');
                        else mapped[k] = String(v);
                    }
                    // set serverErrors so child displays them under inputs
                    setServerErrors(mapped);
                    return;
                }

                // Non-field error (string or other) -> show in AlertDialog
                const message = typeof errBody === 'string' ? errBody : `HTTP ${resp.status}`;
                setAlertTitle('Failed to create role');
                setAlertMessage(message);
                setAlertType('error');
                setAlertOpen(true);
                return;
            }

            // Success
            const data = await resp.json().catch(() => ({}));
            setServerErrors(null);
            const successMsg = data.message || (isEdit ? 'Role updated successfully.' : 'Role created successfully.');
            setAlertTitle('Success');
            setAlertMessage(successMsg);
            setAlertType('success');
            setAlertOpen(true);
        } catch (err) {
            if (err && err.name === 'AbortError') {
                // user cancelled / navigated away — don't show network error
                return;
            }
            setAlertTitle('Network Error');
            setAlertMessage(err.message || 'Could not contact server. Please try again later.');
            setAlertType('error');
            setAlertOpen(true);
        } finally {
            setProgressOpen(false);
            setSubmitting(false);
            controllerRef.current = null;
        }
    };

    const handleCancel = () => {
        // abort inflight request if any
        if (controllerRef.current) {
            controllerRef.current.abort();
            controllerRef.current = null;
        }
        // go back to application details (or parent page)
        if (id) {
            navigate(`/dashboard/applications/${id}/roles`);
        } else {
            navigate('/dashboard/applications');
        }
        // window.location.reload();
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        // on success, navigate back to application's details (adjust if you want different behavior)
        if (alertType === 'success') {
            if (id) {
                navigate(`/dashboard/applications/${id}/roles`);
            } else {
                navigate('/dashboard/applications');
            }
        }
        // window.location.reload();
    };

    return (
        <>
            <CreateEditRoleComponent onSubmit={handleSubmit} onCancel={handleCancel} serverErrors={serverErrors} initialData={initialData} isEdit={isEdit} />

            <ProgressBarDialog open={progressOpen} text_message={progressMessage} cancellable={false} />

            <AlertDialog
                open={alertOpen}
                title={alertTitle}
                message={alertMessage}
                type={alertType}
                okLabel="OK"
                showCancel={false}
                onOk={handleAlertClose}
                onCancel={() => setAlertOpen(false)}
                mini_mode={false}
            />
        </>
    );
}